import React, {useState, ReactNode} from 'react';

import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';

import bem from 'client/services/bem';
import {useLanguage} from 'client/services/hooks';
import {arrayMoveImmutable} from 'client/services/utils/array-move';

import AppButton from 'client/common/buttons/app-button/app-button';
import Icon from 'client/common/icon';
import TextInput from 'client/common/inputs/text-input';

import {Translation} from 'client/models/language/types';

import cssModule from './string-list-input.module.scss';

const b = bem('string-list-input', {cssModule});

type SortableProps = {
  children: ReactNode;
};

const SortableList = SortableContainer(({children}: SortableProps) => <div>{children}</div>);

const SortableItem = SortableElement(({children}: SortableProps) => <div>{children}</div>);

const DragHandle = SortableHandle(({children}: SortableProps) => <span>{children}</span>);

type Props = {
  label?: Translation;
  value?: string[];
  name?: string;
  onChange?: (value: string[]) => void;
};

const StringListInput: React.FC<Props> = (props) => {
  const {label, value, name, onChange} = props;
  const lang = useLanguage('COMMON.INPUTS.STRING_LIST_INPUT');
  const [newValue, setNewValue] = useState('');

  const valueList = value || [];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newValue) {
      onChange?.([...valueList, newValue]);
      setNewValue('');
    }
  };

  const handleRemove = (indexToRemove: number) => {
    onChange?.(valueList.filter((_, index) => index !== indexToRemove));
  };

  const handleSortEnd = ({oldIndex, newIndex}: {oldIndex: number; newIndex: number}) => {
    const newList = arrayMoveImmutable(valueList, oldIndex, newIndex);
    onChange?.(newList);
  };

  const placeholder = valueList.length ? lang.TYPE_NEW_VALUE : lang.PLACEHOLDER;

  return (
    <div className={b()}>
      {label && (
        <label htmlFor={name} className={b('label')}>
          {label}
        </label>
      )}
      <div className={b('content', {shift: !!valueList.length})}>
        <SortableList onSortEnd={handleSortEnd} helperClass={b('drag-helper')} useDragHandle>
          {valueList.map((item, index) => (
            <SortableItem key={index} index={index}>
              <p className={b('list-item')}>
                <DragHandle>
                  <span className={b('list-item-text')}>{item}</span>
                </DragHandle>
                <AppButton
                  className={b('remove-button')}
                  label={<Icon name="close" width={12} height={12} />}
                  onClick={() => handleRemove(index)}
                  asWrap
                />
              </p>
            </SortableItem>
          ))}
        </SortableList>
        <TextInput
          className={b('new-value')}
          classNames={{input: b('new-value-input')}}
          value={newValue}
          name={name}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />
      </div>
      <p className={b('support-text')}>{lang.SUPPORT_TEXT}</p>
    </div>
  );
};

export default StringListInput;
